import { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, where, deleteDoc, doc, updateDoc, getDocs, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { notifyNewMessage } from '../utils/notifications';
import CreateGroup from './CreateGroup';
import GroupSettings from './GroupSettings';
import MeetingRoom from './MeetingRoom';
import Message from './Message';
import './Chats.css';

function Chats({ user, userData }) {
  const [groups, setGroups] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [showMeetingRoom, setShowMeetingRoom] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Cleanup non-existent users from all groups
  const cleanupAllGroups = async (groupsList) => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const existingUserIds = usersSnapshot.docs.map(doc => doc.id);
      
      for (const group of groupsList) {
        const nonExistentUsers = (group.members || []).filter(
          memberId => !existingUserIds.includes(memberId)
        );
        
        if (nonExistentUsers.length > 0) {
          console.log(`🧹 Cleaning ${nonExistentUsers.length} non-existent user(s) from group: ${group.name}`);
          
          const groupRef = doc(db, 'groups', group.id);
          for (const userId of nonExistentUsers) {
            await updateDoc(groupRef, {
              members: arrayRemove(userId),
              admins: arrayRemove(userId)
            });
          }
        }
      }
    } catch (error) {
      console.error('Error cleaning up groups:', error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch groups where user is a member
  useEffect(() => {
    const q = query(collection(db, 'groups'));
    
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const groupsData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(group => group.members?.includes(user.uid))
        .sort((a, b) => {
          // Sort: default groups first (like "General"), then by name
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          if (a.name === 'General' && b.name !== 'General') return -1;
          if (a.name !== 'General' && b.name === 'General') return -1;
          return a.name.localeCompare(b.name);
        });
      
      setGroups(groupsData);
      
      // Auto-cleanup non-existent users from all groups (admin only)
      if (isAdmin) {
        cleanupAllGroups(groupsData);
      }
      
      // Auto-select first group on desktop if none selected
      if (!selectedChat && groupsData.length > 0 && !isMobileView) {
        setSelectedChat(groupsData[0].id);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user.uid, selectedChat, isMobileView]);

  // Fetch messages for selected chat
  useEffect(() => {
    if (!selectedChat) return;

    const q = query(
      collection(db, 'messages'),
      where('chatId', '==', selectedChat),
      orderBy('createdAt', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
    });

    return () => unsubscribe();
  }, [selectedChat]);

  // Fetch group members for mentions
  useEffect(() => {
    if (!selectedChat) return;

    const selectedGroup = groups.find(g => g.id === selectedChat);
    if (!selectedGroup) return;

    // Fetch user details for all members
    const fetchMembers = async () => {
      const membersQuery = query(collection(db, 'users'));
      const unsubscribe = onSnapshot(membersQuery, (snapshot) => {
        const allUsers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter to only group members
        const members = allUsers.filter(u => selectedGroup.members?.includes(u.id));
        setGroupMembers(members);
      });

      return unsubscribe;
    };

    fetchMembers();
  }, [selectedChat, groups]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Check for @ mention
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      // Check if there's a space after @, if so, don't show mentions
      if (!textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt.toLowerCase());
        setShowMentions(true);
        return;
      }
    }

    setShowMentions(false);
  };

  const handleMentionSelect = (member) => {
    const cursorPosition = inputRef.current.selectionStart;
    const textBeforeCursor = newMessage.substring(0, cursorPosition);
    const textAfterCursor = newMessage.substring(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    const mentionText = member.id === 'everyone' ? '@everyone ' : `@${member.name} `;
    const newText = 
      newMessage.substring(0, lastAtIndex) + 
      mentionText + 
      textAfterCursor;

    setNewMessage(newText);
    setShowMentions(false);
    
    // Focus back on input
    setTimeout(() => {
      inputRef.current?.focus();
      const newCursorPos = lastAtIndex + mentionText.length;
      inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const filteredMentions = [
    // Add @everyone option at the top
    { id: 'everyone', name: 'everyone', role: 'Mention all members' },
    // Then filter regular members
    ...groupMembers.filter(member => 
      member.name.toLowerCase().includes(mentionSearch) && 
      member.id !== user.uid
    )
  ].filter(member => 
    member.id === 'everyone' || member.name.toLowerCase().includes(mentionSearch)
  );

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedImage) || !selectedChat) return;

    const messageText = newMessage;
    const imageData = selectedImage;
    setNewMessage(''); // Clear input immediately for better UX
    removeImage(); // Clear image

    try {
      const messageData = {
        text: messageText || '',
        userId: user.uid,
        userName: userData?.name || user.email.split('@')[0],
        chatId: selectedChat,
        createdAt: serverTimestamp()
      };

      // Add image if present
      if (imageData) {
        messageData.image = imageData;
        messageData.hasImage = true;
      }

      // Add reply reference if replying
      if (replyingTo) {
        messageData.replyTo = {
          messageId: replyingTo.id,
          text: replyingTo.text,
          userName: replyingTo.userName
        };
        setReplyingTo(null); // Clear reply state
      }

      // Check for @everyone mention
      const hasEveryoneMention = /@everyone\b/i.test(messageText);

      // Extract mentions from message (only match username without spaces)
      const mentionRegex = /@(\w+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(messageText)) !== null) {
        const mentionedName = match[1].toLowerCase();
        
        // Skip @everyone as it's handled separately
        if (mentionedName === 'everyone') continue;
        
        const mentionedUser = groupMembers.find(m => 
          m.name.toLowerCase() === mentionedName.toLowerCase()
        );
        if (mentionedUser) {
          mentions.push({
            userId: mentionedUser.id,
            userName: mentionedUser.name
          });
        }
      }

      // Store mentions in message
      if (mentions.length > 0 || hasEveryoneMention) {
        messageData.mentions = mentions;
        if (hasEveryoneMention) {
          messageData.everyoneMentioned = true;
        }
      }

      await addDoc(collection(db, 'messages'), messageData);

      // Send push notification if there are mentions or @everyone
      if (mentions.length > 0 || hasEveryoneMention) {
        notifyNewMessage(
          selectedChat,
          user.uid,
          userData?.name || user.email.split('@')[0],
          messageText || (imageData ? '📷 Image' : ''),
          [],
          mentions,
          hasEveryoneMention
        ).catch(err => console.error('Failed to send notification:', err));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageText); // Restore message on error
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    // Focus input after setting reply
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedMessages([]);
  };

  const toggleMessageSelection = (messageId) => {
    setSelectedMessages(prev => 
      prev.includes(messageId)
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedMessages.length === 0) return;
    
    if (!window.confirm(`Delete ${selectedMessages.length} message(s)?`)) return;

    try {
      // Delete all selected messages
      await Promise.all(
        selectedMessages.map(messageId => 
          deleteDoc(doc(db, 'messages', messageId))
        )
      );
      
      setSelectedMessages([]);
      setSelectMode(false);
    } catch (error) {
      console.error('Error deleting messages:', error);
      alert('Failed to delete messages');
    }
  };

  const handlePinMessage = async (messageId) => {
    if (!isAdmin) return;

    try {
      const messageRef = doc(db, 'messages', messageId);
      const message = messages.find(m => m.id === messageId);
      
      // Toggle pin status
      await updateDoc(messageRef, {
        isPinned: !message.isPinned,
        pinnedAt: !message.isPinned ? serverTimestamp() : null,
        pinnedBy: !message.isPinned ? user.uid : null
      });
    } catch (error) {
      console.error('Error pinning message:', error);
      alert('Failed to pin message');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleGroupCreated = (newGroup) => {
    setSelectedChat(newGroup.id);
  };

  const handleBackToList = () => {
    setSelectedChat(null);
  };

  const handleGroupDeleted = () => {
    setSelectedChat(null);
  };

  const selectedGroupData = groups.find(group => group.id === selectedChat);
  const isCreator = selectedGroupData?.createdBy === user.uid;
  const isAdmin = userData?.role === 'admin';
  const canManageGroup = isCreator || isAdmin; // Admins can manage any group


  if (loading) {
    return (
      <div className="chats-container">
        <div className="spinner"></div>
      </div>
    );
  }

  // Mobile view: show either list or chat
  if (isMobileView) {
    if (selectedChat) {
      // Show chat view
      return (
        <div className="chats-container mobile-chat-view">
          <div className="chat-main">
            <div className="chat-header mobile-header">
              <button onClick={handleBackToList} className="back-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="chat-header-info">
                <h1>{selectedGroupData?.name}</h1>
                <span className="members-count">{selectedGroupData?.members?.length || 0} members</span>
              </div>
              {isAdmin && !selectMode && (
                <button onClick={toggleSelectMode} className="select-btn" title="Select Messages">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </button>
              )}
              {selectMode && (
                <>
                  <button onClick={handleDeleteSelected} className="delete-btn" title="Delete Selected" disabled={selectedMessages.length === 0}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {selectedMessages.length > 0 && <span>({selectedMessages.length})</span>}
                  </button>
                  <button onClick={toggleSelectMode} className="cancel-select-btn" title="Cancel">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </>
              )}
              {!selectMode && (
                <>
                  {/* <button onClick={() => setShowMeetingRoom(true)} className="meeting-btn" title="Join Meeting">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button> */}
                  {canManageGroup && (
                    <button onClick={() => setShowGroupSettings(true)} className="settings-btn" title="Group Settings">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="messages-list">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p>No messages yet</p>
                  <span>Start the conversation!</span>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <Message
                      key={message.id}
                      message={message}
                      currentUserId={user.uid}
                      onReply={handleReply}
                      selectMode={selectMode}
                      isSelected={selectedMessages.includes(message.id)}
                      onToggleSelect={toggleMessageSelection}
                      onPin={handlePinMessage}
                      isAdmin={isAdmin}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            <form onSubmit={handleSendMessage} className="message-input-form">
              {replyingTo && (
                <div className="replying-to-bar">
                  <div className="replying-to-content">
                    <span className="replying-label">Replying to {replyingTo.userName}</span>
                    <span className="replying-text">
                      {replyingTo.text.length > 30 
                        ? replyingTo.text.substring(0, 30) + '...' 
                        : replyingTo.text}
                    </span>
                  </div>
                  <button type="button" onClick={cancelReply} className="cancel-reply-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {imagePreview && (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                  <button type="button" onClick={removeImage} className="remove-image-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              <div className="input-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                <button 
                  type="button" 
                  onClick={() => fileInputRef.current?.click()} 
                  className="image-btn"
                  title="Attach Image"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
                <div className="input-wrapper">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newMessage}
                    onChange={handleInputChange}
                    placeholder={replyingTo ? "Type your reply..." : "Type a message... (use @ to mention)"}
                    className="message-input"
                  />
                  {showMentions && filteredMentions.length > 0 && (
                    <div className="mentions-dropdown">
                      {filteredMentions.map(member => (
                        <button
                          key={member.id}
                          type="button"
                          className="mention-item"
                          onClick={() => handleMentionSelect(member)}
                        >
                          <div className="mention-avatar">
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="mention-info">
                            <span className="mention-name">{member.name}</span>
                            <span className="mention-role">{member.role}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="submit" className="send-btn" disabled={!newMessage.trim() && !selectedImage}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </form>
          </div>

          {showGroupSettings && selectedGroupData && (
            <GroupSettings
              group={selectedGroupData}
              user={user}
              userData={userData}
              onClose={() => setShowGroupSettings(false)}
              onGroupDeleted={handleGroupDeleted}
            />
          )}

          {showMeetingRoom && selectedGroupData && (
            <MeetingRoom
              groupId={selectedGroupData.id}
              groupName={selectedGroupData.name}
              user={user}
              userData={userData}
              onClose={() => setShowMeetingRoom(false)}
            />
          )}
        </div>
      );
    } else {
      // Show groups list
      return (
        <div className="chats-container mobile-list-view">
          <div className="chats-sidebar mobile-sidebar">
            <div className="sidebar-header">
              <h2>Groups</h2>
              {isAdmin && (
                <button onClick={() => setShowCreateGroup(true)} className="create-group-btn" title="Create Group">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              )}
            </div>
            
            <div className="chat-list">
              {groups.length === 0 ? (
                <div className="no-groups">
                  <p>No groups yet</p>
                  {isAdmin && (
                    <button onClick={() => setShowCreateGroup(true)} className="create-first-group-btn">
                      Create Group
                    </button>
                  )}
                </div>
              ) : (
                groups.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => setSelectedChat(group.id)}
                    className="chat-item"
                  >
                    <div className="chat-avatar">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="chat-info">
                      <div className="chat-name-row">
                        <span className="chat-name">{group.name}</span>
                        {group.isPinned && (
                          <svg className="pin-icon" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M16 12V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                          </svg>
                        )}
                      </div>
                      <span className="group-members">{group.members?.length || 0} members</span>
                    </div>
                    <svg className="chevron-right" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))
              )}
            </div>
          </div>

          {showCreateGroup && (
            <CreateGroup
              user={user}
              userData={userData}
              onClose={() => setShowCreateGroup(false)}
              onGroupCreated={handleGroupCreated}
            />
          )}
        </div>
      );
    }
  }

  // Desktop view: show both sidebar and chat
  return (
    <div className="chats-container">
      <div className="chats-sidebar">
        <div className="sidebar-header">
          <h2>Groups</h2>
          {isAdmin && (
            <button onClick={() => setShowCreateGroup(true)} className="create-group-btn" title="Create Group">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
        </div>
        
        <div className="chat-list">
          {groups.length === 0 ? (
            <div className="no-groups">
              <p>No groups yet</p>
              {isAdmin && (
                <button onClick={() => setShowCreateGroup(true)} className="create-first-group-btn">
                  Create Group
                </button>
              )}
            </div>
          ) : (
            groups.map((group) => (
              <button
                key={group.id}
                onClick={() => setSelectedChat(group.id)}
                className={`chat-item ${selectedChat === group.id ? 'active' : ''}`}
              >
                <div className="chat-avatar">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="chat-info">
                  <div className="chat-name-row">
                    <span className="chat-name">{group.name}</span>
                    {group.isPinned && (
                      <svg className="pin-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 12V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                      </svg>
                    )}
                  </div>
                  <span className="group-members">{group.members?.length || 0} members</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="chat-main">
        {selectedChat ? (
          <>
            <div className="chat-header">
              <div className="chat-header-info">
                <h1>{selectedGroupData?.name}</h1>
                <span className="members-count">{selectedGroupData?.members?.length || 0} members</span>
              </div>
              <div className="chat-header-actions">
                {isAdmin && !selectMode && (
                  <button onClick={toggleSelectMode} className="select-btn" title="Select Messages">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </button>
                )}
                {selectMode && (
                  <>
                    <button onClick={handleDeleteSelected} className="delete-btn" title="Delete Selected" disabled={selectedMessages.length === 0}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      {selectedMessages.length > 0 && <span>({selectedMessages.length})</span>}
                    </button>
                    <button onClick={toggleSelectMode} className="cancel-select-btn" title="Cancel">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                )}
                {!selectMode && (
                  <>
                    {/* <button onClick={() => setShowMeetingRoom(true)} className="meeting-btn" title="Join Meeting">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button> */}
                    {canManageGroup && (
                      <button onClick={() => setShowGroupSettings(true)} className="settings-btn" title="Group Settings">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="messages-list">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p>No messages yet</p>
                  <span>Start the conversation!</span>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <Message
                      key={message.id}
                      message={message}
                      currentUserId={user.uid}
                      onReply={handleReply}
                      selectMode={selectMode}
                      isSelected={selectedMessages.includes(message.id)}
                      onToggleSelect={toggleMessageSelection}
                      onPin={handlePinMessage}
                      isAdmin={isAdmin}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            <form onSubmit={handleSendMessage} className="message-input-form">
              {replyingTo && (
                <div className="replying-to-bar">
                  <div className="replying-to-content">
                    <span className="replying-label">Replying to {replyingTo.userName}</span>
                    <span className="replying-text">
                      {replyingTo.text.length > 30 
                        ? replyingTo.text.substring(0, 30) + '...' 
                        : replyingTo.text}
                    </span>
                  </div>
                  <button type="button" onClick={cancelReply} className="cancel-reply-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {imagePreview && (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                  <button type="button" onClick={removeImage} className="remove-image-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              <div className="input-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                <button 
                  type="button" 
                  onClick={() => fileInputRef.current?.click()} 
                  className="image-btn"
                  title="Attach Image"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
                <div className="input-wrapper">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newMessage}
                    onChange={handleInputChange}
                    placeholder={replyingTo ? "Type your reply..." : "Type a message... (use @ to mention)"}
                    className="message-input"
                  />
                  {showMentions && filteredMentions.length > 0 && (
                    <div className="mentions-dropdown">
                      {filteredMentions.map(member => (
                        <button
                          key={member.id}
                          type="button"
                          className="mention-item"
                          onClick={() => handleMentionSelect(member)}
                        >
                          <div className="mention-avatar">
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="mention-info">
                            <span className="mention-name">{member.name}</span>
                            <span className="mention-role">{member.role}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="submit" className="send-btn" disabled={!newMessage.trim() && !selectedImage}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="no-chat-selected">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p>Select a group to start chatting</p>
          </div>
        )}
      </div>

      {showCreateGroup && (
        <CreateGroup
          user={user}
          userData={userData}
          onClose={() => setShowCreateGroup(false)}
          onGroupCreated={handleGroupCreated}
        />
      )}

      {showGroupSettings && selectedGroupData && (
        <GroupSettings
          group={selectedGroupData}
          user={user}
          userData={userData}
          onClose={() => setShowGroupSettings(false)}
          onGroupDeleted={handleGroupDeleted}
        />
      )}

      {showMeetingRoom && selectedGroupData && (
        <MeetingRoom
          groupId={selectedGroupData.id}
          groupName={selectedGroupData.name}
          user={user}
          userData={userData}
          onClose={() => setShowMeetingRoom(false)}
        />
      )}
    </div>
  );
}

export default Chats;


